import { useMemo } from 'react';

/**
 * CSS-only drifting gold dust. Parameters are stable per-mount via useMemo
 * so positions don't reshuffle on re-render.
 */
type Props = {
  count?: number;
  className?: string;
};

type Spec = {
  x: number;
  delay: number;
  dur: number;
  sway: number;
  size: number;
};

function rand(seed: number) {
  // tiny deterministic-ish PRNG to keep particle pattern stable across renders
  let s = seed >>> 0;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 0xffffffff;
  };
}

export function ParticleField({ count = 50, className }: Props) {
  const particles = useMemo<Spec[]>(() => {
    const r = rand(42);
    return Array.from({ length: count }, () => ({
      x: r() * 100,
      delay: r() * 18,
      dur: 8 + r() * 16,
      sway: (r() - 0.5) * 80,
      size: 2 + Math.round(r() * 2),
    }));
  }, [count]);

  return (
    <div className={`ow-particles ${className ?? ''}`} aria-hidden="true">
      {particles.map((p, i) => (
        <span
          key={i}
          className="ow-particle"
          style={
            {
              left: `${p.x}%`,
              width: `${p.size}px`,
              height: `${p.size}px`,
              ['--delay' as never]: `${-p.delay}s`,
              ['--dur' as never]: `${p.dur}s`,
              ['--sway' as never]: `${p.sway}px`,
            } as React.CSSProperties
          }
        />
      ))}
    </div>
  );
}
